import { baseApi } from 'core/api';
import { useToastStore } from 'store/modules';

const state = () => ({
    _settingsVoice: {
        record: false,
        setupRecord: false,
        devices: [],
        devicesInput: [],
        devicesOutInput: [],
        audioInput: null,
        audioOutInput: null,
        token: null,
    },
    _showCallWidget: false,
    _contactFromCallWidget: null,
    _contactToCallWidget: null,
    _timeTwilio: 0,
    _directionStatus: null,
    _connectStatus: null,
    _loadingTwilio: true,
    _loadingCallTransfer: false,
    _loadingCallWaiting: false,
    _loadingConferenceCalling: false,
    _loadingConferenceCallingHoldStatus: false,
    _conferenceName: null,
    _conferenceSid: null,
    _conferenceParticipantUpdate: null,
    _isHoldConferenceCalling: false,
    _loadingCallEnd: false,
    _activeIncomingMute: false,
    _testSettingsVoice: {
        devices: [],
        devicesInput: [],
        devicesOutInput: [],
        audioInput: null,
        audioOutInput: null,
    },
    _voicemailDrops: [],
    _loadingVoicemailDrops: false,
});

const getters = {
    twilioToken: (state) => state._settingsVoice.token,
    twilioRecord: (state) => state._settingsVoice.record,
    twilioDevices: (state) => state._settingsVoice.devices,
    twilioDevicesInput: (state) => state._settingsVoice.devicesInput,
    twilioDevicesOutInput: (state) => state._settingsVoice.devicesOutInput,
    twilioAudioOutInput: (state) => state._settingsVoice.audioOutInput,
    twilioAudioInput: (state) => state._settingsVoice.audioInput,
    twilioSetupRecord: (state) => state._settingsVoice.setupRecord,
    showCallWidget: (state) => state._showCallWidget,
    contactFromCallWidget: (state) => state._contactFromCallWidget,
    contactToCallWidget: (state) => state._contactToCallWidget,
    timeTwilio: (state) => state._timeTwilio,
    directionStatusTwilio: (state) => state._directionStatus,
    connectStatusTwilio: (state) => state._connectStatus,
    loadingTwilio: (state) => state._loadingTwilio,
    loadingCallTransfer: (state) => state._loadingCallTransfer,
    loadingCallWaiting: (state) => state._loadingCallWaiting,
    loadingConferenceCalling: (state) => state._loadingConferenceCalling,
    loadingConferenceCallingHoldStatus: (state) => state._loadingConferenceCallingHoldStatus,
    conferenceName: (state) => state._conferenceName,
    conferenceSid: (state) => state._conferenceSid,
    conferenceParticipantUpdate: (state) => state._conferenceParticipantUpdate,
    isHoldConferenceCalling: (state) => state._isHoldConferenceCalling,
    loadingCallEnd: (state) => state._loadingCallEnd,
    activeIncomingMute: (state) => state._activeIncomingMute,
    voicemailDrops: (state) => state._voicemailDrops,
    loadingVoicemailDrops: (state) => state._loadingVoicemailDrops,

    testDevices: (state) => state._testSettingsVoice.devices,
    testDevicesInput: (state) => state._testSettingsVoice.devicesInput,
    testDevicesOutInput: (state) => state._testSettingsVoice.devicesOutInput,
    testAudioOutInput: (state) => state._testSettingsVoice.audioOutInput,
    testAudioInput: (state) => state._testSettingsVoice.audioInput,
};

const actions = {
    createVoiceToken(number) {
        return baseApi
            .post('core/voice/token', {
                number: number,
            })
            .then((response) => {
                this._setTwilioCreateToken(response.data.token);

                return response.data.token;
            });
    },

    initTwilioCallWidget(data) {
        this._setTwilioInitCallWidget(data);
    },

    showTwilioCallWidget(status) {
        this._setTwilioShowCallWidget(status);
    },

    incrementTimeTwilio(reset) {
        this._setTwilioIncrementTime(reset);
    },

    updateSettingsVoiceTwilio(data) {
        this._setTwilioUpdateSettingsVoice(data);
    },

    updateStateVoiceTwilio(data) {
        this._setTwilioUpdateStateVoice(data);
    },

    setCallTransfer({ callSid, inboxId = null, number = null }) {
        this._setVoiceStateUpdate({
            loadingCallTransfer: true,
        });

        return baseApi
            .post('core/voice/call/transfer', {
                call_sid: callSid,
                inbox_id: inboxId,
                number: number,
            })
            .catch((error) => {
                if (error.response?.status === 422) {
                    useToastStore().showToast({
                        type: 'danger',
                        message: error.response?.data?.message,
                    });
                } else {
                    useToastStore().showToast({
                        type: 'danger',
                        message: 'Transfer initiation failed. Please try again',
                    });
                }
            })
            .finally(() => {
                this._setVoiceStateUpdate({
                    loadingCallTransfer: false,
                });
            });
    },

    updateVoiceHoldStatus({ isFirstHold, isSendSegmentEvent = true }) {
        this._setVoiceStateUpdate({
            loadingCallWaiting: true,
        });

        return baseApi
            .post('core/voice/call/hold', {
                call_sid: window.connect.parameters.CallSid,
                is_first_hold: isFirstHold,
                send_segment_event: isSendSegmentEvent,
            })
            .then((response) => {
                return response.data.success;
            })
            .finally(() => {
                this._setVoiceStateUpdate({
                    loadingCallWaiting: false,
                });
            });
    },

    updateVoiceUnholdStatus({ isSendSegmentEvent = true }) {
        this._setVoiceStateUpdate({
            loadingCallWaiting: true,
        });

        return baseApi
            .post('core/voice/call/unhold', {
                call_sid: window.connect.parameters.CallSid,
                send_segment_event: isSendSegmentEvent,
            })
            .then((response) => {
                return response.data.success;
            })
            .finally(() => {
                this._setVoiceStateUpdate({
                    loadingCallWaiting: false,
                });
            });
    },

    updateVoiceRecordStatus({ callSid, record }) {
        let status = record;

        if (record === true) status = 'on';
        if (record === false) status = 'off';

        return baseApi.post('core/voice/record/status', {
            call_id: callSid,
            record: status,
        });
    },

    getVoiceRecordById(id) {
        return baseApi
            .get(`core/voice/voicemail-record/${id}`, {
                responseType: 'blob',
            })
            .then((response) => {
                return response.data;
            });
    },

    getVoiceRecordURL(url) {
        return fetch(url).then((r) => r.blob());
    },

    callEnd() {
        this._setVoiceStateUpdate({
            loadingCallEnd: true,
        });

        return baseApi
            .post('core/voice/call/call-end', {
                call_sid: window.connect.parameters.CallSid,
            })
            .then((response) => {
                return response.data.success;
            })
            .catch((e) => {
                return e.response;
            })
            .finally(() => {
                this._setVoiceStateUpdate({
                    loadingCallEnd: false,
                });
            });
    },

    createConferenceCalling({
        inboxId = null,
        number = null,
        contactId = null,
        labelFrom,
        labelTo,
        currentInboxId,
    }) {
        this._setVoiceStateUpdate({
            loadingConferenceCalling: true,
        });

        return baseApi
            .post(`core/voice/call/${window.connect.parameters.CallSid}/conference`, {
                active_call: {
                    from: labelFrom,
                    to: labelTo,
                },
                new_participant: {
                    inbox_id: inboxId,
                    number,
                    contact_id: contactId,
                },
                current_inbox_id: currentInboxId,
            })
            .then((response) => {
                if (response.data?.success) {
                    this._setVoiceStateUpdate({
                        conferenceName: response?.data?.conference_name,
                        conferenceSid: response?.data?.conference_sid,
                    });
                }

                return response.data;
            })
            .catch((e) => {
                useToastStore().showToast({
                    type: 'danger',
                    message: e.response.data.message,
                });

                return e.response;
            })
            .finally(() => {
                this._setVoiceStateUpdate({
                    loadingConferenceCalling: false,
                });
            });
    },

    addParticipantToConferenceCalling({
        inboxId = null,
        number = null,
        contactId = null,
        currentInboxId,
        conferenceName,
        conferenceSid,
    }) {
        if (!conferenceSid) return;

        this._setVoiceStateUpdate({
            loadingConferenceCalling: true,
        });

        return baseApi
            .put(
                `core/voice/call/${window.connect.parameters.CallSid}/conference/${conferenceSid}`,
                {
                    new_participant: {
                        inbox_id: inboxId,
                        number,
                        contact_id: contactId,
                    },
                    current_inbox_id: currentInboxId,
                    conference_name: conferenceName,
                }
            )
            .then((response) => {
                return response.data;
            })
            .catch((e) => {
                useToastStore().showToast({
                    type: 'danger',
                    message: e.response.data.message,
                });

                return e.response;
            })
            .finally(() => {
                this._setVoiceStateUpdate({
                    loadingConferenceCalling: false,
                });
            });
    },

    updateConferenceParticipant({ data }) {
        this._setVoiceStateUpdate({
            conferenceParticipantUpdate: data,
        });
    },

    setConferenceCallingHold({ conferenceSid }) {
        if (!conferenceSid) return;

        this._setVoiceStateUpdate({
            loadingConferenceCallingHoldStatus: true,
        });

        return baseApi
            .put(`core/voice/conference/${conferenceSid}/hold`)
            .then((response) => {
                this._setVoiceStateUpdate({
                    isHoldConferenceCalling: response.data.success,
                });

                return response.data.success;
            })
            .finally(() => {
                this._setVoiceStateUpdate({
                    loadingConferenceCallingHoldStatus: false,
                });
            });
    },

    setConferenceCallingResume({ conferenceSid }) {
        if (!conferenceSid) return;

        this._setVoiceStateUpdate({
            loadingConferenceCallingHoldStatus: true,
        });

        return baseApi
            .put(`core/voice/conference/${conferenceSid}/resume`)
            .then((response) => {
                this._setVoiceStateUpdate({
                    isHoldConferenceCalling: !response.data.success,
                });

                return response.data.success;
            })
            .finally(() => {
                this._setVoiceStateUpdate({
                    loadingConferenceCallingHoldStatus: false,
                });
            });
    },

    resetConferenceCallingStatus() {
        this._setVoiceStateUpdate({
            conferenceName: null,
            conferenceSid: null,
            conferenceParticipantUpdate: null,
            isHoldConferenceCalling: false,
        });
    },

    setConferenceCallingParticipantAction({ conferenceSid, actionType, participantLabel }) {
        return baseApi
            .put(`core/voice/conference/${conferenceSid}/participant`, {
                action: actionType,
                participant: participantLabel,
            })
            .then((response) => {
                return response.data.success;
            });
    },

    conferenceCallingEndAll({ conferenceSid }) {
        if (!conferenceSid) return;

        this._setVoiceStateUpdate({
            loadingCallEnd: true,
        });

        return baseApi
            .put(`core/voice/conference/${conferenceSid}/end`)
            .then((response) => {
                return response.data.success;
            })
            .catch((e) => {
                return e.response;
            })
            .finally(() => {
                this._setVoiceStateUpdate({
                    loadingCallEnd: false,
                });
            });
    },
    sendCallQualityLog({ callSid, logData }) {
        return baseApi
            .post(`core/voice/call/${callSid}/quality/log`, logData)
            .then((response) => {
                return response.data.success;
            })
            .catch((e) => {
                return e.response;
            });
    },

    fetchVoicemailDropsList() {
        this._setVoiceStateUpdate({
            loadingVoicemailDrops: true,
        });
        return baseApi
            .get('core/voice/voicemail/drops')
            .then((response) => {
                this._setVoiceStateUpdate({
                    voicemailDrops: response.data,
                });
                return response.data;
            })
            .catch((e) => {
                return e.response;
            })
            .finally(() => {
                this._setVoiceStateUpdate({
                    loadingVoicemailDrops: false,
                });
            });
    },

    createVoicemailDrop({ formData, cancelToken }) {
        this._setVoiceStateUpdate({
            loadingVoicemailDrops: true,
        });
        return baseApi
            .post('core/voice/voicemail/drops/upload', formData, {
                cancelToken: cancelToken,
            })
            .then((response) => {
                this._setVoiceStateAddVoicemailDrop(response.data);
                return response.data;
            })
            .catch((e) => {
                return e.response;
            })
            .finally(() => {
                this._setVoiceStateUpdate({
                    loadingVoicemailDrops: false,
                });
            });
    },

    updateVoicemailDropById({ id, name }) {
        this._setVoiceStateUpdate({
            loadingVoicemailDrops: true,
        });
        return baseApi
            .put(`core/voice/voicemail/drops/update/${id}`, {
                name,
            })
            .then((response) => {
                return response.data;
            })
            .catch((e) => {
                return e.response;
            })
            .finally(() => {
                this._setVoiceStateUpdate({
                    loadingVoicemailDrops: false,
                });
            });
    },

    deleteVoicemailDropById({ id }) {
        this._setVoiceStateUpdate({
            loadingVoicemailDrops: true,
        });
        return baseApi
            .delete(`core/voice/voicemail/drops/delete/${id}`)
            .then(() => {
                this._setVoiceStateRemoveVoicemailDrop(id);
            })
            .catch((e) => {
                return e.response;
            })
            .finally(() => {
                this._setVoiceStateUpdate({
                    loadingVoicemailDrops: false,
                });
            });
    },

    updateSettingsVoice(data) {
        this._setUpdateSettingsVoice(data);
    },

    _setTwilioChangeStatusRecord(data) {},

    _setTwilioUpdateStateVoice([key, value]) {
        this[`_${key}`] = value;
    },

    _setTwilioUpdateSettingsVoice([key, value]) {
        this._settingsVoice[key] = value;
    },

    _setTwilioIncrementTime(reset) {
        if (reset) {
            this._timeTwilio = 0;
        } else {
            this._timeTwilio += 1000;
        }
    },

    _setTwilioCreateToken(token) {
        this._settingsVoice.token = token;
    },

    _setTwilioInitCallWidget(data) {
        this._contactFromCallWidget = data.contactFrom;
        this._contactToCallWidget = data.contactTo;
    },

    _setTwilioShowCallWidget(status) {
        this._showCallWidget = status;
    },

    _setVoiceStateAddVoicemailDrop(data) {
        this._voicemailDrops.push(data);
    },

    _setVoiceStateRemoveVoicemailDrop(id) {
        this._voicemailDrops = this._voicemailDrops.filter((item) => item.id !== id);
    },

    _setVoiceStateUpdate(data) {
        for (let key in data) {
            this[`_${key}`] = data[key];
        }
    },

    _setUpdateSettingsVoice([key, value]) {
        this._testSettingsVoice[key] = value;
    },
};

export default {
    state,
    getters,
    actions,
};
